import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ORDER_DEVICES_MIN_VALUE } from '../../../../constants';
import { FetchStatus, ModalName } from '../../../../enums';
import { useCloseModal } from '../../../../features/modals';
import {
  useOrderOrganizationDevice,
  useOrganization,
  useResetOrderOrganizationDevicesStatus,
} from '../../../../features/organization';
import { Button, Input, Modal } from '../../../common';

export const OrderDevicesModal = () => {
  const { t } = useTranslation();
  const {
    status: { orderDevices: status },
  } = useOrganization();
  const orderOrganizationDevices = useOrderOrganizationDevice();
  const resetOrganizationOrderDevicesStatus = useResetOrderOrganizationDevicesStatus();
  const [amount, setAmount] = useState(0);
  const [completed, setCompleted] = useState(false);
  const closeModal = useCloseModal(ModalName.ORDER_DEVICES);

  const send = useCallback(() => {
    orderOrganizationDevices(amount);
  }, [orderOrganizationDevices, amount]);

  useEffect(() => {
    if (status === FetchStatus.SUCCESS) {
      setCompleted(true);
      resetOrganizationOrderDevicesStatus();
    }
  }, [status]);

  const footer: JSX.Element = useMemo(() => {
    return completed ? (
      <div className="flex">
        <Button text={t('modal.orderDevices.button.ok')} onClick={closeModal} />
      </div>
    ) : (
      <div className="flex">
        <Button
          text={t('modal.orderDevices.button.send')}
          disabled={amount < ORDER_DEVICES_MIN_VALUE}
          loading={status === FetchStatus.LOADING}
          onClick={send}
        />
        <Button text={t('modal.orderDevices.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, send, completed, closeModal, amount, status]);

  return (
    <Modal
      name={ModalName.ORDER_DEVICES}
      header={t(`modal.orderDevices.title.${completed ? 'completed' : 'initial'}`)}
      footer={footer}
    >
      {!completed && (
        <Input
          type="number"
          value={`${amount}`}
          onChange={(value) => setAmount(+value)}
          label={t('modal.orderDevices.field.amount.label')}
          minValue={0}
          hasError={status === FetchStatus.FAILURE}
          className="mb-4"
        />
      )}
      <span className="typography-paragraph-3 text-text-secondary whitespace-pre-line">
        {t(`modal.orderDevices.description.${completed ? 'completed' : 'initial'}`)}
      </span>
    </Modal>
  );
};
